import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CREATE_DOCUMENT } from '@/entities/document';
import { pathLabel, useSections } from '@/entities/section';
import { type Workspace } from '@/entities/workspace';
import { Combobox } from '@/shared/ui';

import styles from './CreateDocument.module.css';

/** Workspaces where the viewer may create a note (the server checks again). */
export const noteDestinations = (workspaces: readonly Workspace[]) =>
  workspaces.filter((w) => w.permissions.includes(CREATE_DOCUMENT));

/**
 * Where a new note goes: the workspace and the section. A place already known (the workspace page
 * knows its open section or group) is shown as plain text instead of being asked again.
 */
export function useDocumentPlace({
  workspaces,
  workspaceId,
  sectionId,
  groupId,
}: {
  workspaces: readonly Workspace[];
  workspaceId?: string | undefined;
  sectionId?: string | null | undefined;
  groupId?: string | null | undefined;
}) {
  const { t } = useTranslation();
  const preset = workspaceId !== undefined && (sectionId != null || groupId != null);
  const [target, setTarget] = useState(workspaceId ?? workspaces[0]?.id ?? '');
  const [section, setSection] = useState<string>(sectionId ?? '');
  const sections = useSections(target);
  const workspace = workspaces.find((w) => w.id === target);
  const nameOf = (w: Workspace) =>
    w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name;
  const sectionOptions = useMemo(
    () => [
      { value: '', label: t('documents.place.root') },
      ...(sections.data?.items ?? []).map((s) => ({
        value: s.id,
        label: pathLabel(sections.data?.items ?? [], s.id),
      })),
    ],
    [sections.data, t],
  );
  const picker = (
    <>
      {workspaces.length > 1 && !preset && (
        <Combobox
          label={t('documents.place.workspace')}
          value={target}
          options={workspaces.map((w) => ({ value: w.id, label: nameOf(w) }))}
          onChange={(next) => {
            setTarget(next);
            setSection(''); // sections belong to one workspace
          }}
        />
      )}
      {!preset && (sections.data?.items.length ?? 0) > 0 && (
        <Combobox
          label={t('documents.place.section')}
          value={section}
          options={sectionOptions}
          onChange={setSection}
        />
      )}
      {preset && workspace && (
        <p className={styles.where}>
          {t('documents.place.willBeIn', {
            place: [nameOf(workspace), pathLabel(sections.data?.items ?? [], sectionId)]
              .filter(Boolean)
              .join(' / '),
          })}
        </p>
      )}
    </>
  );
  return {
    workspace,
    picker,
    /** What the create / import request carries for the place. */
    body: {
      ...(groupId ? { group_id: groupId } : {}),
      ...(!groupId && (preset ? sectionId : section)
        ? { section_id: preset ? sectionId : section }
        : {}),
      ...(groupId && sectionId ? { section_id: sectionId } : {}),
    } as { group_id?: string; section_id?: string },
  };
}
